import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { WorkItemObservableCacheService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/work-item-observable-cache.service';
import { VideoSessionStatus } from 'common/common/constants/video-session-status';
import { VideoService } from 'shared/services/video-service/video.service';
import { Chat } from 'common/xislivechat/models/chat';
import { Subscription, BehaviorSubject, Subject, timer, Observable } from 'rxjs';
import { distinctUntilChanged, delayWhen, scan, takeWhile, takeUntil, repeatWhen } from 'rxjs/operators';
import { videoChatStatusBubbleAnimation } from './animations/video-chat-status-bubble.animations';
import wasStarted = VideoSessionStatus.wasStarted;
import hasEnded = VideoSessionStatus.hasEnded;

export const HIDE_ENDED_MESSAGE_DELAY = 5000;
export const COUNTER_INTERVAL = 1000;

@Component({
  selector: 'video-chat-status-bubble',
  templateUrl: './video-chat-status-bubble.component.html',
  animations: [videoChatStatusBubbleAnimation],
  styleUrls: ['./video-chat-status-bubble.component.scss']
})
export class VideoChatStatusBubbleComponent implements OnInit, OnDestroy {
  @Input()
  workItemId: string;
  @Input()
  workItemType: string;

  translateParams = { name: '' };
  counter = 0;
  hasEnded = false;

  private _chat: Chat;
  private _workItemSubscription: Subscription;
  private _timerSubscription: Subscription;
  private _hasEndedSubjectSubscription: Subscription;
  private _hasEndedSubject$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _start = new Subject<void>();
  private _stop = new Subject<void>();
  private _timer$: Observable<number> = timer(0, COUNTER_INTERVAL).pipe(
    scan(acc => --acc, (HIDE_ENDED_MESSAGE_DELAY / COUNTER_INTERVAL) + 1),
    takeWhile(x => x >= 0),
    // eslint-disable-next-line rxjs/no-unsafe-takeuntil
    takeUntil(this._stop),
    // eslint-disable-next-line rxjs/no-ignored-notifier
    repeatWhen(() => this._start)
  );

  constructor(private _workItemObservableCacheService: WorkItemObservableCacheService,
    private _videoService: VideoService) {
  }

  ngOnInit(): void {
    if (this.workItemType === WorkItemTypes.CHAT) {
      this._workItemSubscription = this._workItemObservableCacheService.getWorkItemObservable(this.workItemId, this.workItemType)
        .subscribe(chat => {
          this._chat = chat;
          this.translateParams = { name: chat.ChatName };
          this._hasEndedSubject$.next(hasEnded(this._chat.VideoSessionStatus));
        });

      this._handleHideEndedMessage();
    }
  }

  ngOnDestroy(): void {
    if (this._workItemSubscription) {
      this._workItemSubscription.unsubscribe();
      this._workItemSubscription = null;
    }
    this._timerSubscription?.unsubscribe();
    this._hasEndedSubjectSubscription?.unsubscribe();
  }

  get bubbleStatus(): string {
    switch (this._chat.VideoSessionStatus) {
      case VideoSessionStatus.InitiatedByVisitor:
        return 'videoChatStatusBubble.initiatedByVisitor';
      case VideoSessionStatus.InitiatedByOperator:
        return 'videoChatStatusBubble.initiatedByOperator';
      case VideoSessionStatus.AcceptedByOperator:
      case VideoSessionStatus.AcceptedByVisitor:
        return 'videoChatStatusBubble.accepted';
      case VideoSessionStatus.DeclinedByOperator:
        return 'videoChatStatusBubble.declinedByOperator';
      case VideoSessionStatus.DeclinedByVisitor:
        return 'videoChatStatusBubble.declinedByVisitor';
      case VideoSessionStatus.EndedByOperator:
      case VideoSessionStatus.EndedByVisitor:
      case VideoSessionStatus.EndedByServer:
        return 'videoChatStatusBubble.ended';
    }
  }

  get buttonText(): string {
    return this._chat.VideoSessionStatus === VideoSessionStatus.InitiatedByVisitor
      ? 'videoChatStatusBubble.join'
      : 'videoChatStatusBubble.showCall';
  }

  get isVisible(): boolean {
    return this._chat && wasStarted(this._chat.VideoSessionStatus) && this._chat.VideoSessionStatus !== VideoSessionStatus.InitiatedByVisitor;
  }

  get showJoinButton(): boolean {
    return this._chat && VideoSessionStatus.isInSession(this._chat.VideoSessionStatus);
  }

  actionButtonClicked(): void {
    this._videoService.focusOrOpenVideoWindow(this._chat);
  }

  private _handleHideEndedMessage() {
    this._timer$.subscribe((tick) => this.counter = tick);
    this._hasEndedSubjectSubscription = this._hasEndedSubject$.pipe(
      distinctUntilChanged(),
      delayWhen((ended) => this._handleDelay(ended))
    ).subscribe((ended) => this.hasEnded = ended);
  }

  private _handleDelay(ended: boolean) {
    if (ended) {
      this._start.next();
    } else {
      this._stop.next();
      this.counter = 0;
    }

    return ended ? timer(HIDE_ENDED_MESSAGE_DELAY).pipe(takeUntil(this._stop)) : timer(0);
  }
}
